import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Lock, Loader2, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { getApiError } from '@/lib/utils';

export default function ChangePasswordPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (formData.new_password !== formData.confirm_password) {
      setError('New passwords do not match');
      return;
    }
    
    // Validate password length
    if (formData.new_password.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    
    setSaving(true);
    
    try {
      await api.post('/auth/change-password', {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      
      toast({ 
        title: 'Success', 
        description: 'Password changed successfully. Please log in with your new password.' 
      });
      
      // Log out and redirect to login
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 1500);
      
    } catch (err) {
      setError(getApiError(err, 'Failed to change password'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Toaster />
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-6 w-6 text-amber-600" />
          </div>
          <CardTitle>Change Your Password</CardTitle>
          <CardDescription>
            {user?.must_change_password ? (
              <div className="flex items-center gap-2 text-amber-600 justify-center mt-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Password change required for security</span>
              </div>
            ) : (
              'Update your password to continue'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={formData.current_password}
                onChange={(e) => handleChange('current_password', e.target.value)}
                placeholder="Enter your temporary/current password"
                required
                data-testid="current-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={formData.new_password}
                onChange={(e) => handleChange('new_password', e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={formData.confirm_password}
                onChange={(e) => handleChange('confirm_password', e.target.value)}
                placeholder="Repeat new password"
                required
                data-testid="confirm-password-input"
              />
            </div>
            
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={saving}
              data-testid="change-password-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Change Password
            </Button>
            
            <p className="text-xs text-muted-foreground text-center">
              After changing your password, you will be logged out and need to sign in again.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
